import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material"
import {
  RESELLER_APPLICATION_DOCUMENT_KINDS,
  RESELLER_APPLICATION_DOCUMENT_LABELS,
  type ResellerApplicationDocumentKind,
} from "@xop/dashboard-shared"
import { Download, FileText, Lock, ShieldCheck, Trash2, Upload } from "lucide-react"
import { useCallback, useRef, useState, type JSX } from "react"

import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { PdfReader } from "@/components/pdf/pdf-reader"
import { useToast } from "@/components/ui/use-toast"
import { ResellerNetworkColumn } from "@crm-src/components/reseller-network-column"
import { FileTypeIcon } from "@crm-src/components/file-manager/file-type-icon"
import usePermissions from "@crm-src/hooks/usePermissions"
import { useResellerPagedList } from "@crm-src/hooks/useResellerPagedList"
import { formatFileSize } from "@crm-src/lib/file-type-utils"
import {
  resellerAttachmentsApi,
  type ResellerAttachment,
} from "@crm-src/lib/reseller-attachments-api"
import {
  downloadApplicationDocument,
  fetchApplicationDocumentBytes,
  useResellerOnboarding,
} from "@crm-src/pages/administration/resellers/applications/useResellerApplications"

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
}

/**
 * Files — the reseller's paperwork, from BOTH sources, in one place.
 *
 *   1. The company CABINET (`/resellers/:id/attachments`) — anything staff or
 *      the reseller filed. Upload, download, preview, delete.
 *   2. The ONBOARDING documents — the agreement and NDA the applicant signed in
 *      the portal, and XOP's countersigned copies. READ-ONLY here.
 *
 * The second list is read-only on purpose. Those four blobs belong to a
 * `ResellerApplication` and the application page is the one place that writes
 * them; a second writer would mean two ways to replace a signed agreement and
 * no single answer to "which one is current". One place to write, two places to
 * read — the same rule the Onboarding card on Overview already follows.
 *
 * PDFs preview through the shared `<PdfReader>` (pdf.js → canvas). Never a
 * `blob:` or cross-origin iframe: that hangs the macOS desktop shell.
 */
export function ResellerFilesTab({ resellerId }: { resellerId: string }): JSX.Element {
  const { toast } = useToast()
  const { canManageResellers } = usePermissions()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<ResellerAttachment | null>(null)
  const [preview, setPreview] = useState<{ filename: string; bytes: ArrayBuffer } | null>(null)

  const onboarding = useResellerOnboarding(Number(resellerId))
  const applications = onboarding.data?.applications ?? []

  const files = useResellerPagedList<ResellerAttachment>(
    useCallback(
      async ({ page, perPage, search }) => {
        const result = await resellerAttachmentsApi.list(resellerId, {
          page,
          per_page: perPage,
          ...(search ? { search } : {}),
        })
        return {
          rows: result.attachments ?? [],
          totalCount: result.pagination?.total_count ?? 0,
          totalPages: result.pagination?.total_pages ?? 1,
        }
      },
      [resellerId],
    ),
  )

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0]
    // Reset the input FIRST so picking the same file twice in a row still fires
    // a change event.
    event.target.value = ""
    if (!file) return

    setUploading(true)
    try {
      const result = await resellerAttachmentsApi.upload(resellerId, file)
      if (!result.success) throw new Error(result.error || "Upload failed")
      toast({ title: "Uploaded", description: `${file.name} was added to this reseller.` })
      files.refresh()
    } catch (error) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (error as Error).message ||
        "Failed to upload the file"
      toast({ variant: "destructive", title: "Upload failed", description: message })
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (attachment: ResellerAttachment): Promise<void> => {
    try {
      await resellerAttachmentsApi.remove(resellerId, attachment.id)
      toast({ title: "Removed", description: `${attachment.name} was deleted.` })
      files.refresh()
    } catch (error) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        "Failed to remove the file"
      toast({ variant: "destructive", title: "Error", description: message })
    }
  }

  const previewAttachment = async (attachment: ResellerAttachment): Promise<void> => {
    try {
      const bytes = await resellerAttachmentsApi.bytes(resellerId, attachment.id)
      setPreview({ filename: attachment.name, bytes })
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Couldn't open that file." })
    }
  }

  const previewOnboardingDoc = async (
    applicationId: number,
    kind: ResellerApplicationDocumentKind,
  ): Promise<void> => {
    try {
      const bytes = await fetchApplicationDocumentBytes(applicationId, kind)
      setPreview({ filename: `${kind}.pdf`, bytes })
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Couldn't open that document." })
    }
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={handleUpload}
        data-cy="reseller-file-input"
        aria-label="Upload a file for this reseller"
      />

      <ResellerNetworkColumn<ResellerAttachment>
        data-cy="reseller-files-panel"
        title="Files"
        icon={<FileText size={18} />}
        entityNoun="files"
        emptyMessage="No files filed against this reseller yet"
        list={files}
        {...(canManageResellers()
          ? {
              action: (
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  startIcon={<Upload size={14} />}
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  data-cy="reseller-file-upload"
                >
                  {uploading ? "Uploading…" : "Upload"}
                </Button>
              ),
            }
          : {})}
        renderRow={(file) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              p: 1.5,
              bgcolor: "surfaceSoft",
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1 }}>
              <FileTypeIcon file={{ name: file.name, content_type: file.content_type }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {file.name}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25, flexWrap: "wrap" }}>
                  <Chip
                    size="small"
                    label={file.uploaded_by_kind === "reseller" ? "Filed by the reseller" : "Filed by XOP"}
                    color={file.uploaded_by_kind === "reseller" ? "info" : "default"}
                    variant="outlined"
                  />
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    {formatFileSize(file.file_size)} · {formatDate(file.created_at)} · {file.uploaded_by.name}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
              {file.content_type === "application/pdf" && (
                <Tooltip title="Preview">
                  <IconButton size="small" onClick={() => previewAttachment(file)} aria-label={`Preview ${file.name}`}>
                    <FileText size={14} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Download">
                <IconButton
                  size="small"
                  aria-label={`Download ${file.name}`}
                  onClick={() => resellerAttachmentsApi.download(resellerId, file.id, file.name)}
                >
                  <Download size={14} />
                </IconButton>
              </Tooltip>
              {/* `deletable` is the SERVER's verdict, computed from the same
                  predicate DELETE enforces — never re-derived here, or the
                  button and the endpoint could disagree. */}
              {canManageResellers() && file.deletable && (
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    aria-label={`Delete ${file.name}`}
                    sx={{ color: "error.main", "&:hover": { color: "error.dark" } }}
                    onClick={() => setPendingDelete(file)}
                  >
                    <Trash2 size={14} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        )}
      />

      {applications.length > 0 && (
        <Card data-cy="reseller-onboarding-documents" data-testid="reseller-onboarding-documents">
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.125rem", fontWeight: 600 }}>
                <Box sx={{ color: "text.secondary", display: "flex" }}><ShieldCheck size={20} /></Box>
                <span>Signed onboarding documents</span>
              </Box>
            }
            subheader={
              <Typography sx={{ fontSize: 12, color: "text.secondary", display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                <Lock size={12} />
                Read-only here — these belong to the application, which is where they are replaced.
              </Typography>
            }
          />
          <CardContent sx={{ display: "grid", gap: 1.5 }}>
            {applications.map((application) =>
              RESELLER_APPLICATION_DOCUMENT_KINDS.filter((kind) => application.documents?.[kind]?.attached).map((kind) => (
                <Box
                  key={`${application.id}-${kind}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    p: 1.5,
                    bgcolor: "surfaceSoft",
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <FileTypeIcon file={{ name: "document.pdf", content_type: "application/pdf" }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                      {RESELLER_APPLICATION_DOCUMENT_LABELS[kind]}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                    <Tooltip title="Preview">
                      <IconButton
                        size="small"
                        aria-label={`Preview ${RESELLER_APPLICATION_DOCUMENT_LABELS[kind]}`}
                        onClick={() => previewOnboardingDoc(application.id, kind)}
                      >
                        <FileText size={14} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        aria-label={`Download ${RESELLER_APPLICATION_DOCUMENT_LABELS[kind]}`}
                        onClick={() => downloadApplicationDocument(application.id, kind, `${kind}.pdf`)}
                      >
                        <Download size={14} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )),
            )}
          </CardContent>
        </Card>
      )}

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Delete file"
        variant="destructive"
        confirmLabel="Delete"
        description={
          pendingDelete
            ? `Delete ${pendingDelete.name}? This removes the file permanently and cannot be undone.`
            : ""
        }
        onConfirm={async () => {
          if (pendingDelete) await handleDelete(pendingDelete)
        }}
      />

      <Dialog open={preview !== null} onClose={() => setPreview(null)} maxWidth="lg" fullWidth>
        <DialogTitle>{preview?.filename}</DialogTitle>
        <DialogContent>
          <PdfReader bytes={preview?.bytes ?? null} filename={preview?.filename ?? "document.pdf"} />
        </DialogContent>
      </Dialog>
    </Box>
  )
}
